/**
 * CNP (Cod Numeric Personal) Romanian Validator
 * Frontend implementation
 */

interface CNPValidationResult {
  valid: boolean;
  error?: string;
  dateOfBirth?: Date;
  sex?: 'M' | 'F';
}

export class CNPValidator {
  private static readonly CONTROL_KEY = '279146358279';

  /**
   * Validate CNP and extract info
   */
  static validate(cnp: string): CNPValidationResult {
    // Remove spaces and non-digits
    const cleanCNP = cnp.replace(/\s/g, '');

    // Check length
    if (cleanCNP.length !== 13) {
      return { valid: false, error: 'CNP trebuie să aibă 13 cifre' };
    }

    // Check all digits
    if (!/^\d{13}$/.test(cleanCNP)) {
      return { valid: false, error: 'CNP trebuie să conțină doar cifre' };
    }

    // Extract components
    const s = parseInt(cleanCNP[0]);
    const aa = parseInt(cleanCNP.substring(1, 3));
    const ll = parseInt(cleanCNP.substring(3, 5));
    const zz = parseInt(cleanCNP.substring(5, 7));
    const controlDigit = parseInt(cleanCNP[12]);

    // Validate sex digit
    if (s < 1 || s > 8) {
      return { valid: false, error: 'Prima cifră CNP invalidă' };
    }

    // Determine century and sex
    let year: number;
    let sex: 'M' | 'F';

    switch (s) {
      case 1:
      case 2:
        year = 1900 + aa;
        sex = s === 1 ? 'M' : 'F';
        break;
      case 3:
      case 4:
        year = 1800 + aa;
        sex = s === 3 ? 'M' : 'F';
        break;
      case 5:
      case 6:
        year = 2000 + aa;
        sex = s === 5 ? 'M' : 'F';
        break;
      case 7:
      case 8:
        year = 2000 + aa;
        sex = s === 7 ? 'M' : 'F';
        break;
      default:
        return { valid: false, error: 'Cod sex invalid' };
    }

    // Validate month
    if (ll < 1 || ll > 12) {
      return { valid: false, error: 'Luna invalidă în CNP' };
    }

    // Validate day
    if (zz < 1 || zz > 31) {
      return { valid: false, error: 'Ziua invalidă în CNP' };
    }

    // Create date
    const dateOfBirth = new Date(year, ll - 1, zz);

    // Validate date is real
    if (
      dateOfBirth.getFullYear() !== year ||
      dateOfBirth.getMonth() !== ll - 1 ||
      dateOfBirth.getDate() !== zz
    ) {
      return { valid: false, error: 'Data nașterii invalidă' };
    }

    // Validate control digit
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(cleanCNP[i]) * parseInt(this.CONTROL_KEY[i]);
    }
    const calculatedControl = sum % 11 === 10 ? 1 : sum % 11;

    if (calculatedControl !== controlDigit) {
      return { valid: false, error: 'CNP invalid (cifră de control)' };
    }

    return {
      valid: true,
      dateOfBirth,
      sex,
    };
  }

  /**
   * Extract date of birth from CNP
   */
  static extractDateOfBirth(cnp: string): Date | null {
    const result = this.validate(cnp);
    return result.valid ? result.dateOfBirth || null : null;
  }

  /**
   * Extract sex from CNP
   */
  static extractSex(cnp: string): 'M' | 'F' | null {
    const result = this.validate(cnp);
    return result.valid ? result.sex || null : null;
  }

  /**
   * Format CNP for display (groups of 1-2-2-2-3-3)
   */
  static format(cnp: string): string {
    const clean = cnp.replace(/\s/g, '');
    if (clean.length <= 1) return clean;
    if (clean.length <= 3) return `${clean.slice(0, 1)} ${clean.slice(1)}`;
    if (clean.length <= 5) return `${clean.slice(0, 1)} ${clean.slice(1, 3)} ${clean.slice(3)}`;
    if (clean.length <= 7) return `${clean.slice(0, 1)} ${clean.slice(1, 3)} ${clean.slice(3, 5)} ${clean.slice(5)}`;
    if (clean.length <= 10) return `${clean.slice(0, 1)} ${clean.slice(1, 3)} ${clean.slice(3, 5)} ${clean.slice(5, 7)} ${clean.slice(7)}`;
    return `${clean.slice(0, 1)} ${clean.slice(1, 3)} ${clean.slice(3, 5)} ${clean.slice(5, 7)} ${clean.slice(7, 10)} ${clean.slice(10)}`;
  }
}
